import React, { useState, useEffect } from 'react'
import { Box, Button, Checkbox, Stack, Typography } from '@mui/material'
import { Search, User, Building2 } from 'lucide-react'
import { COMPANY_TILE_RADIUS } from 'components/UI'
import { DrawerPopup } from 'components/UI/DrawerPopup'
import { FieldTrigger } from 'components/UI/FieldTrigger'
import { Input } from 'components/UI/Input'
import { MENU_ROW_LABEL_SX, menuRowToneSx } from 'components/UI/menuRowTone'
import { createApiCall } from 'services'
import { border, font } from 'settings/tokens';

export type ProvenanceOwner = {
  id: string
  name?: string
  email?: string
  avatar?: string
  /**
   * LOWERCASE — these are the raw `ProvenanceHistoryItemType` values the API sends
   * (`'customer'` / `'company'`), copied through verbatim by the loader below. The
   * declaration said `'Customer' | 'Company'`, which never matched a single value at
   * runtime; the only reason it went unnoticed is that nothing compared against it
   * (the row prints it through `textTransform: 'capitalize'`, which is itself the
   * evidence the stored value is lowercase).
   */
  owner_type: 'customer' | 'company'
}

export type ProvenanceOwnerSearchPickerProps = {
  mobile?: boolean
  open: boolean
  onOpen: () => void
  onClose: () => void
  certificateId: string
  selectedOwnerIds: string[]
  onToggleOwnerId: (id: string, checked: boolean) => void
  onClear: () => void
  onApply: () => void
}

export const ProvenanceOwnerSearchPicker = ({
  mobile,
  open,
  onOpen,
  onClose,
  certificateId,
  selectedOwnerIds,
  onToggleOwnerId,
  onClear,
  onApply,
}: ProvenanceOwnerSearchPickerProps) => {
  const [query, setQuery] = useState('')
  const [owners, setOwners] = useState<ProvenanceOwner[]>([])
  const [loading, setLoading] = useState(false)
  const [showAllChips, setShowAllChips] = useState(false)

  useEffect(() => {
    if (open && certificateId) {
      fetchOwners()
    }
  }, [open, certificateId])

  const fetchOwners = async () => {
    setLoading(true)
    try {
      const { data, status } = await createApiCall({
        method: 'GET',
        url: `certificates/public/provenance/${certificateId}?limit=100`,
      })
      
      if (status === 200 && data?.data) {
        const uniqueOwners = new Map<string, ProvenanceOwner>()
        
        data.data.forEach((item: any) => {
          let ownerId: string
          let ownerName: string
          let ownerEmail: string | undefined
          let ownerAvatar: string | undefined

          if (item.owner_type === 'customer') {
            ownerId = item.owner.email || `customer-${Math.random()}`
            // VERA-304: empty, not 'Unknown'. A customer with no name is labelled by
            // their email at render time (see the rows below); fabricating a name here
            // would put a placeholder above the only real identifier the row has.
            ownerName = `${item.owner.first_name || ''} ${item.owner.last_name || ''}`.trim()
            ownerEmail = item.owner.email
            ownerAvatar = item.owner.avatar
          } else {
            ownerId = `company-${item.owner.name || item.certificate?.company?.name || 'Unknown'}`
            ownerName = item.owner.name || item.certificate?.company?.name || 'Unknown Company'
            ownerEmail = undefined
            ownerAvatar = item.owner.logo || item.certificate?.company?.logo
          }

          if (!uniqueOwners.has(ownerId)) {
            uniqueOwners.set(ownerId, {
              id: ownerId,
              name: ownerName,
              email: ownerEmail,
              avatar: ownerAvatar,
              owner_type: item.owner_type,
            })
          }
        })
        
        setOwners(Array.from(uniqueOwners.values()))
      } else {
        setOwners([])
      }
    } catch (error) {
      console.error('Error fetching owners:', error)
      setOwners([])
    } finally {
      setLoading(false)
    }
  }

  const filteredOwners = owners.filter(owner => 
    (owner.name?.toLowerCase().includes(query.toLowerCase()) || 
     owner.email?.toLowerCase().includes(query.toLowerCase()))
  )

  const selectedOwners = owners.filter(owner => selectedOwnerIds.includes(owner.id))

  const content = (
    <Stack sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      <Stack spacing={1.5} sx={{ flex: 1 }}>
        {/* This file already uses `FieldTrigger` for its two read-only triggers and
            then hand-rolled the editable one three lines away, with a hardcoded 12px
            radius (bypassing the clamp) and a `grey[300]` border instead of the token.
            The base `Input` is the editable half of the same pair, and now shares
            `fieldSurfaceSx` with the trigger literally rather than by copy. */}
        <Input
          value={query}
          onChange={setQuery}
          placeholder='Search owners...'
          startAdornment={<Search size={16} />}
          inputProps={{ 'aria-label': 'Search owners' }}
        />

      {selectedOwnerIds.length > 0 && (
        <Stack spacing={1}>
          <Typography sx={{ color: 'text.primary' }}>Selected items: {selectedOwnerIds.length}</Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5 }}>
            {(showAllChips ? selectedOwners : selectedOwners.slice(0, 6)).map(owner => (
              <Box
                key={owner.id}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                  backgroundColor: theme => theme.palette.grey[50],
                  borderRadius: '12px',
                  px: 1.5,
                  py: 0.75,
                }}
              >
                <Typography sx={{ fontSize: 14 }}>{owner.name || owner.email}</Typography>
                <Box
                  onClick={() => onToggleOwnerId(owner.id, false)}
                  sx={{
                    cursor: 'pointer',
                    fontSize: 16,
                    fontWeight: 'bold',
                  }}
                >
                  ×
                </Box>
              </Box>
            ))}
            {selectedOwnerIds.length > 6 && !showAllChips && (
              <Box
                sx={{
                  cursor: 'pointer',
                  textDecoration: 'underline',
                  color: 'primary.main',
                  fontSize: 14,
                  alignSelf: 'center',
                  px: 1,
                }}
                onClick={() => setShowAllChips(true)}
              >
                Show all
              </Box>
            )}
            {selectedOwnerIds.length > 6 && showAllChips && (
              <Box
                sx={{
                  cursor: 'pointer',
                  textDecoration: 'underline',
                  color: 'primary.main',
                  fontSize: 14,
                  alignSelf: 'center',
                  px: 1,
                }}
                onClick={() => setShowAllChips(false)}
              >
                Show less
              </Box>
            )}
          </Box>
        </Stack>
      )}

        <Box sx={{ flex: 1, overflowY: 'auto', pr: 0.5 }}>
          {loading ? (
            <Typography sx={{ textAlign: 'center', color: 'text.secondary', py: 4 }}>Loading owners...</Typography>
          ) : filteredOwners.length === 0 ? (
            <Typography sx={{ textAlign: 'center', color: 'text.secondary', py: 4 }}>No owners found</Typography>
          ) : (
            <Stack spacing={1.5}>
              {filteredOwners.map(owner => (
                <Stack
                  key={owner.id}
                  direction={'row'}
                  alignItems={'center'}
                  spacing={1.25}
                  sx={theme => ({
                    px: 0.5,
                    // Selection stays the CHECKBOX tick — this row deliberately takes
                    // no selected background. `menuRowToneSx` supplies the other half
                    // of the contract: muted at rest, `text.primary` once ticked,
                    // accent + grey wash on hover/focus (the row had no hover state at
                    // all before).
                    ...menuRowToneSx(theme, { selected: selectedOwnerIds.includes(owner.id) }),
                  })}
                >
                  <Checkbox
                    size="small"
                    checked={selectedOwnerIds.includes(owner.id)}
                    onChange={(e) => onToggleOwnerId(owner.id, e.target.checked)}
                    icon={<Box sx={{ width: 18, height: 18, border: theme => `1px solid ${theme.palette.text.primary}`, borderRadius: '6px', backgroundColor: theme => theme.palette.grey[50] }} />}
                    checkedIcon={<Box sx={{ width: 18, height: 18, borderRadius: '6px', backgroundColor: theme => theme.palette.text.primary, display: 'flex', alignItems: 'center', justifyContent: 'center' }}><Box component="span" sx={{ width: 10, height: 6, borderBottom: theme => `2px solid ${theme.palette.common.white}`, borderLeft: theme => `2px solid ${theme.palette.common.white}`, transform: 'rotate(-45deg)', mt: '-1px' }} /></Box>}
                    sx={{ p: 0, '& .MuiTouchRipple-root': { display: 'none' }, '&:hover': { backgroundColor: 'transparent' } }}
                  />
                  {/* This list mixes brands and people in one column, so the shape is
                      read off `owner_type` rather than being uniform: a brand's logo in
                      a square tile, a person in a circle. The no-image fallback sits in
                      the SAME box, so it inherits the shape and the column never shows
                      a square logo above a circular placeholder for another brand. */}
                  <Box sx={{
                    width: 36,
                    height: 36,
                    borderRadius: owner.owner_type === 'company' ? COMPANY_TILE_RADIUS : '50%',
                    backgroundColor: theme =>
                      owner.owner_type === 'company' && owner.avatar
                        ? theme.palette.common.white
                        : theme.palette.grey[200],
                    border: theme => `1px solid ${theme.palette.grey[300]}`,
                    overflow: 'hidden',
                    flex: '0 0 36px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    p: owner.owner_type === 'company' ? '3px' : 0,
                    boxSizing: 'border-box',
                  }}>
                    {owner.avatar ? (
                      <img
                        src={owner.avatar}
                        alt={owner.name}
                        style={
                          owner.owner_type === 'company'
                            ? // `contain`, so a wide wordmark or a tall lockup is
                              // letterboxed inside the tile rather than stretched.
                              { maxWidth: '100%', maxHeight: '100%', width: 'auto', height: 'auto', objectFit: 'contain', display: 'block' }
                            : { width: '100%', height: '100%', objectFit: 'cover' }
                        }
                      />
                    ) : owner.owner_type === 'company' ? (
                      <Building2 size={18} color={'#8B8D8F'} />
                    ) : (
                      <User size={18} color={'#8B8D8F'} />
                    )}
                  </Box>
                  <Stack spacing={0}>
                    <Typography sx={{ ...MENU_ROW_LABEL_SX, fontSize: 14, fontWeight: 500 }}>{owner.name || owner.email}</Typography>
                    {/* Both lines below are genuinely secondary (an address and a type
                        label) and hold `text.secondary` through every row state. */}
                    {/* VERA-304: only when it is not already the primary line above. */}
                    {owner.name && owner.email && (
                      <Typography sx={{ fontSize: font.label, color: 'text.secondary' }}>{owner.email}</Typography>
                    )}
                    <Typography sx={{ fontSize: font.label, color: 'text.secondary', textTransform: 'capitalize' }}>
                      {owner.owner_type}
                    </Typography>
                  </Stack>
                </Stack>
              ))}
            </Stack>
          )}
        </Box>
      </Stack>

      <Box sx={{ px: 2, py: 1.5 }}>
        <Button variant="text" onClick={onClear} fullWidth sx={{ borderRadius: '12px', border: theme => `1px solid ${theme.palette.grey[300]}`, height: 40, color: 'text.primary' }}>
          Clear
        </Button>
      </Box>
    </Stack>
  )

  if (mobile) {
    return (
      <>
        {/* VERA-315: the shared trigger. This was hand-rolled — 12px radius, 40px
            tall, 14px placeholder — so the Owner field sat directly above a Scan
            date and a Location field that are 6px/44px/16px. Same sheet, three
            fields, two treatments. */}
        <FieldTrigger icon={<Search size={16} />} placeholder='Search' onClick={onOpen} />

        <DrawerPopup
          title="Select owners"
          rightActionLabel="Done"
          rightActionOnClick={onApply}
          anchor="bottom"
          child={content}
          handleOpen={open}
          handleClose={onClose}
          // Content-height, like every other sheet in the app — this was one of
          // the last `100vh` full-page takeovers.
          paperSx={{ height: 'auto', maxHeight: '90vh' }}
        />
      </>
    )
  }

  // Desktop version (popover-style similar to CertificatesSearchPicker)
  return (
    <Box sx={{ position: 'relative' }}>
      {/* Desktop branch — the same hand-rolled trigger existed twice in this one
          file, so the divergence shipped on both breakpoints. */}
      <FieldTrigger icon={<Search size={16} />} placeholder='Search' onClick={onOpen} />

        {open && (
          <DrawerPopup
            title="Select owners"
            rightActionLabel="Done"
            rightActionOnClick={onApply}
            anchor="right"
            child={content}
            handleOpen={open}
            handleClose={onClose}
            paperSx={{ width: 400, maxWidth: '80vw', mr: 2, my: 2, borderRadius: '32px', height: 'calc(100% - 32px)' }}
          />
        )}
    </Box>
  )
}

export default ProvenanceOwnerSearchPicker

